import { reactive, computed, unref, Ref, toRefs, ref, watch } from '@vue/composition-api';

import { Route } from 'vue-router';
import { useMutation } from 'vue-query';

import { useContext } from '@berlin-front/common/composables/context';
import { useRouter } from '@berlin-front/common/composables/useRouter';
import { ValueTypeOf, getRefDeep, notNil } from '@berlin-front/common/utils';
import { ROUTE_NAME } from '@/router/route-name';
import { MemberRequestContext } from '@views/member/MemberRequestContext';
import { ApiCacheContext } from '@/services/ApiCacheContext';
import { BerlinBffContext } from '@/services/BerlinBff';
import useFakeProgress from '@berlin-front/common/composables/useFakeProgress';
import useOrAll from '@berlin-front/common/composables/useOrAll';
import { GuestTypePostResponseMemberTypeEnum } from '@netprotections/berlin-bff';
import { AuthMemberConfirmType } from './context';

const useSmsNotify = (): {
  isSuccess: Ref<boolean>;
  isError: Ref<boolean>;
  mutateSmsSend: () => void;
} => {
  const { member } = useContext(BerlinBffContext);
  const { tokens } = useContext(ApiCacheContext);
  const { registerMemberFormState } = useContext(MemberRequestContext);
  const {
    isSuccess,
    isError,
    mutate: mutateSmsSend,
  } = useMutation('member.SmsNotifyPost', () =>
    member.atoneMemberControllerMemberSmsNotify(getRefDeep(tokens, 'requestToken'), {
      phoneNumber: registerMemberFormState.phoneNumber,
      mailAddress: registerMemberFormState.mailAddress,
      resend: false,
    }),
  );
  return { isSuccess, isError, mutateSmsSend };
};

export default (props: {
    smsVerificationPage: ValueTypeOf<typeof ROUTE_NAME> | null | undefined;
    registerPage: ValueTypeOf<typeof ROUTE_NAME>;
  }) =>
  (_$route: Route): AuthMemberConfirmType => ({
    useMemberConfirm: () => {
      const router = useRouter();
      const { guestTypePostResult } = useContext(ApiCacheContext);
      const { registerMemberFormState, sexDivisionRender, prefectureName } = useContext(MemberRequestContext);

      const isLoadingApi = ref(false);

      const member = computed(() => ({
        name: [registerMemberFormState.familyName, registerMemberFormState.givenName].join(' '),
        kana: [registerMemberFormState.familyNameKana, registerMemberFormState.givenNameKana].join(' '),
        sex: sexDivisionRender(registerMemberFormState.sexDivision),
        birthday: [
          registerMemberFormState.birthdayYear,
          registerMemberFormState.birthdayMonth,
          registerMemberFormState.birthdayDay.padStart(2, '0'),
        ].join('-'),
        zip: registerMemberFormState.zip.replace(/(\d{3})(\d{4})/, '$1-$2'),
        address1: [
          unref(prefectureName),
          registerMemberFormState.address2First,
          registerMemberFormState.address2Second,
        ].join(''),
        address2: registerMemberFormState.address3,
        phoneNumber: registerMemberFormState.phoneNumber,
      }));

      const { isSuccess: isSendSmsCodeSucceeded, isError: isSendSmsCodeError, mutateSmsSend } = useSmsNotify();
      const isSendSmsCodeCompleted = useOrAll(isSendSmsCodeSucceeded, isSendSmsCodeError);

      const { progress, completed: isProgressCompleted } = toRefs(
        useFakeProgress(5000, isLoadingApi, isSendSmsCodeCompleted),
      );

      const sendSmsCode = () => {
        if (unref(isLoadingApi) || unref(isSendSmsCodeCompleted)) return;
        isLoadingApi.value = true;
        mutateSmsSend();
      };

      const editMemberInfo = () => {
        void router.replace({ name: props.registerPage });
      };

      watch(isProgressCompleted, (result) => {
        if (result && unref(isSendSmsCodeCompleted)) {
          // smsVerificationPageが指定されている場合 (NP会員ルート) は指定された遷移先に遷移する
          if (props.smsVerificationPage) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            router.replace({ name: props.smsVerificationPage });
            return;
          }

          // 新規、またはメタモルの場合はguestTypePostが呼ばれているはず
          const memberTypeGuest = computed(() => notNil(guestTypePostResult.value?.memberType));
          const MEMBER_TYPE_GUEST = {
            GUEST: GuestTypePostResponseMemberTypeEnum.NUMBER_3,
          } as const;

          if (memberTypeGuest.value === MEMBER_TYPE_GUEST.GUEST) {
            // 仮会員のケース (メタモル)
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            router.replace({ name: ROUTE_NAME.ATONEAUTH_GUEST_SMS_AUTH });
          } else {
            // それ以外 (新規)
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            router.replace({ name: ROUTE_NAME.ATONEAUTH_MEMBER_SMS_AUTH });
          }
          return;
        }
        isLoadingApi.value = false;
      });

      return reactive({
        member,
        messages: reactive([]),
        progress,
        sendSmsCode,
        editMemberInfo,
      });
    },
  });
